import React, { useState, useEffect } from 'react';
import { useParams, useSearchParams, useNavigate, Link } from 'react-router-dom';
import { CheckCircle, XCircle, Loader2, Home, ArrowRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import Layout from '../components/Layout';
import { paymentsApi } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';

const PaymentPage = () => {
  const { bookingId } = useParams();
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!authLoading && !user) {
      navigate('/login');
      return;
    }
    if (user && bookingId) {
      initiatePayment();
    }
  }, [user, authLoading, bookingId]);

  const initiatePayment = async () => {
    try {
      const originUrl = window.location.origin;
      const response = await paymentsApi.createCheckout(bookingId, originUrl);
      // Redirect to Stripe
      window.location.href = response.data.url;
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Payment initiation failed');
      navigate('/dashboard');
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="h-96 flex flex-col items-center justify-center">
          <Loader2 className="w-8 h-8 animate-spin text-trust-blue mb-4" />
          <p className="text-slate-600">Preparing payment...</p>
        </div>
      </Layout>
    );
  }

  return null;
};

const PaymentSuccessPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const [status, setStatus] = useState('checking'); // checking, success, failed
  const [attempts, setAttempts] = useState(0);

  useEffect(() => {
    if (!authLoading && !user) {
      navigate('/login');
      return;
    }
    
    const sessionId = searchParams.get('session_id');
    if (sessionId) {
      pollPaymentStatus(sessionId);
    } else {
      setStatus('failed');
    }
  }, [user, authLoading, searchParams]);

  const pollPaymentStatus = async (sessionId) => {
    const maxAttempts = 5;
    const pollInterval = 2000;

    if (attempts >= maxAttempts) {
      setStatus('failed');
      return;
    }

    try {
      const response = await paymentsApi.getStatus(sessionId);
      
      if (response.data.payment_status === 'paid') {
        setStatus('success');
        return;
      } else if (response.data.status === 'expired') {
        setStatus('failed');
        return;
      }

      // Continue polling
      setTimeout(() => {
        setAttempts(prev => prev + 1);
        pollPaymentStatus(sessionId);
      }, pollInterval);
    } catch (error) {
      setStatus('failed');
    }
  };

  return (
    <Layout>
      <div className="max-w-md mx-auto px-4 py-16" data-testid="payment-success-page">
        {status === 'checking' && (
          <div className="text-center">
            <Loader2 className="w-16 h-16 animate-spin text-trust-blue mx-auto mb-6" />
            <h1 className="font-heading text-2xl font-bold text-slate-900 mb-2">
              Processing Payment
            </h1>
            <p className="text-slate-600">Please wait while we confirm your payment...</p>
          </div>
        )}

        {status === 'success' && (
          <div className="text-center">
            <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="w-10 h-10 text-green-600" />
            </div>
            <h1 className="font-heading text-2xl font-bold text-slate-900 mb-2">
              Payment Successful!
            </h1>
            <p className="text-slate-600 mb-8">
              Your booking has been confirmed. You'll receive a confirmation email shortly.
            </p>
            <div className="space-y-3">
              <Link to="/dashboard">
                <Button className="w-full bg-trust-blue hover:bg-trust-blue-hover rounded-full">
                  Go to Dashboard
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
              <Link to="/browse">
                <Button variant="outline" className="w-full rounded-full">
                  Browse More Properties
                </Button>
              </Link>
            </div>
          </div>
        )}

        {status === 'failed' && (
          <div className="text-center">
            <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <XCircle className="w-10 h-10 text-red-600" />
            </div>
            <h1 className="font-heading text-2xl font-bold text-slate-900 mb-2">
              Payment Failed
            </h1>
            <p className="text-slate-600 mb-8">
              Something went wrong with your payment. Please try again or contact support.
            </p>
            <div className="space-y-3">
              <Link to="/dashboard">
                <Button className="w-full bg-trust-blue hover:bg-trust-blue-hover rounded-full">
                  Go to Dashboard
                </Button>
              </Link>
              <Link to="/">
                <Button variant="outline" className="w-full rounded-full">
                  <Home className="w-4 h-4 mr-2" />
                  Back to Home
                </Button>
              </Link>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
};

export { PaymentPage, PaymentSuccessPage };
export default PaymentPage;
